import React, { useCallback, Dispatch, SetStateAction } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Divider, Drawer, Typography } from '@material-ui/core'

import {
    AtlasWhite_100,
    ColorBaseMulberryBlue,
    AtlasGreyDark,
    ColorBaseWhite,
    ColorEastBay
} from 'assets/styles/variables'
import { addKeyPressHandler } from 'common/util/functions'

// Constants
import i18n from 'common/util/internationalization'

// Icons
import { AtlasIcon } from '@diligentcorp/atlas-react'
import { IconButton } from 'components/shared/StyledComponents'

//Components
import { AtlasButton } from 'components/atlas-material/button'
import { AddActionEditActionButton } from 'components/shared/StyledComponents'
import PreviewTemplateComponent from './preview-template'
import {
    MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH,
    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION,
    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE
} from 'common/constants/constants'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'

const drawerWidth = 658

const useStyles = makeStyles((theme) => ({
    drawer: {
        width: drawerWidth,
        overscrollBehavior: 'contain',
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        },
        '@media (max-width: 768px)': {
            width: '100vw'
        }
    },
    drawerPaper: {
        position: 'fixed',
        width: drawerWidth,
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        },
        '@media (max-width: 768px)': {
            width: '100vw'
        }
    },
    headerTitle: {
        fontWeight: 600,
        fontFamily: 'Source Sans Pro',
        fontSize: '22px',
        lineHeight: '26px',
        margin: 0
    },
    headerDescription: {
        fontWeight: 400,
        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        lineHeight: '20px',
        color: AtlasGreyDark,
        margin: 0,
        marginTop: '4px'
    },

    contentBox: {
        display: 'flex',
        padding: '0 24px 24px 24px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '40px',
        flex: '1 0 0',
        alignSelf: 'stretch'
    },
    defaultTemplateSection: {
        display: 'flex',
        width: '600px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px'
    },
    root: {
        padding: '12px',
        // margin: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        gap: '16px'
    },
    rootContainer: {
        borderRadius: 12,
        // margin: '24px',
        backgroundColor: AtlasWhite_100
    },
    iconBox: {
        display: 'flex',
        alignItems: 'center'
    },
    logo: {
        width: 36,
        height: 36,
        borderRadius: 8,
        display: 'inline-block',
        marginRight: theme.spacing(2)
    },
    textBox: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        fontWeight: 600,
        fontSize: '16px',
        color: '#2e3b55'
    },
    subtitle: {
        color: AtlasGreyDark,
        fontSize: '16px',
        fontWeight: 400
    },
    rightSection: {
        display: 'flex',
        alignItems: 'center'
    },
    downloadButton: {
        fontSize: '14px',
        color: '#455D82',
        lineHeight: '18px',
        fontWeight: 600,
        marginRight: theme.spacing(2),
        textTransform: 'none',
        '&:focus': {
            border: `2px solid ${ColorBaseMulberryBlue}`,
            padding: '5px',
            borderRadius: '40px'
        }
    },
    myIconButton: {
        marginLeft: 'auto',
        '&:focus': {
            border: `2px solid ${ColorBaseMulberryBlue}`,
            padding: '5px',
            borderRadius: '40px',
            height: '35px',
            width: '35px'
        }
    },
    uploadSection: {
        width: '100%'
    },
    uploadStatus: {
        marginTop: '16px'
    },
    templateTitle: {
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '20px', // 125%
        marginBottom: '8px'
    },
    templateUploadTitle: {
        fontSize: '16px',
        fontStyle: 'normal',
        marginTop: '24px',
        fontWeight: 600,
        lineHeight: '20px' // 125%
    },
    templateUploadSubtitle: {
        color: 'var(--Semantic-Color-Type-Muted, #676767)',
        fontSize: '14px',
        lineHeight: '18px', // 128.571%
        marginBottom: '8px'
    },
    templateLearnMore: {
        color: 'var(--Semantic-Color-Link-Default, #385F99)',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: '"Source Sans Pro"',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '18px'
    },
    footerSection: {
        marginTop: 'auto'
    },
    footer: {
        height: '72px',
        backgroundColor: '#fff',
        zIndex: theme.zIndex.drawer + 1,
        flexShrink: 0
    },
    footerComp: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        padding: theme.spacing(2),
        gap: '16px'
    },
    saveTemplate: {
        textTransform: 'none',
        backgroundColor: ColorEastBay,
        display: 'flex',
        color: '#fff',
        borderWidth: '0px',
        borderRadius: '3px',
        letterSpacing: '0.5px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        padding: '8px 12px',
        fontWeight: 600,
        placeContent: 'center',
        gap: '8px',
        minWidth: '168px',
        '&:focus, &:active': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px',
            backgroundColor: `${ColorEastBay}`
        },
        '&:hover': {
            backgroundColor: '#364262',
            color: '#fff',
            cursor: 'pointer'
        }
    },

    headerBox: {
        backgroundColor: '#fff',
        zIndex: theme.zIndex.drawer + 1,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: '16px'
    },
    btnContainer: {
        display: 'flex',
        alignItems: 'center',
        marginRight: '16px',
        textDecoration: 'none'
    },
    body: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
    },
    closeButton: {
        '&:focus, &:active': {
            padding: '2px',
            margin: '2px',
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    },
    templateDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '40px 0px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        [theme.breakpoints.down('sm')]: {
            padding: '16px'
        }
    },
    label: {
        // fontWeight: 500,
        // fontSize: '14px',
        marginBottom: '4px',
        color: 'var(--ColorBaseRichBlack, #1E1E1E)',
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '16px' /* 123.077% */
    },
    textField: {
        width: '100%',
        marginTop: 0,
        '& .MuiOutlinedInput-root': {
            marginTop: 0
        }
    },

    descriptionWrapper: {
        position: 'relative',
        width: '100%'
    }
}))

interface TemplateDrawerProps {
    setIsOpen: Dispatch<SetStateAction<boolean>>
    isOpen: boolean
    imageUrlsData?: string[]
    pageCount?: number
    loading?: boolean
    downloadTemplateFile?: (templateId: string, templateTitle?: string) => Promise<void>
    currentTemplate?: any
    systemTemplate?: any
}

const PreviewTemplateDrawer: React.FC<TemplateDrawerProps> = ({
    setIsOpen,
    isOpen,
    imageUrlsData,
    pageCount,
    loading,
    downloadTemplateFile,
    currentTemplate,
    systemTemplate
}) => {
    const classes = useStyles()

    const toggleDrawer = useCallback(() => {
        setIsOpen(!isOpen)
    }, [isOpen, setIsOpen])

    const truncateTitle = (title, limit = 366) =>
        title?.length > limit ? `${title.slice(0, limit)}...` : title

    const isSystemTemplateMatch = (tpl?: any) => {
        if (!tpl || !systemTemplate) return false
        if (Array.isArray(systemTemplate)) {
            return systemTemplate.some((t) => t?.id === tpl?.id)
        }
        return tpl?.id === systemTemplate?.id
    }

    const getPreviewTitle = () => {
        if (!currentTemplate) {
            return ''
        }
        // Check if this is a system template
        if (isSystemTemplateMatch(currentTemplate)) {
            return currentTemplate?.title === MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE
                ? i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE')
                : i18n.t('MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE')
        }
        // For custom templates, show the template title
        return currentTemplate.title
    }

    const getPreviewDescription = () => {
        if (!currentTemplate) {
            return ''
        }
        // Check if this is a system template
        if (isSystemTemplateMatch(currentTemplate)) {
            const fullText =
                currentTemplate?.description === MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION
                    ? i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION')
                    : i18n.t('MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION')
            return truncateTitle(fullText, MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH)
        }
        // For custom templates, show the template description
        return truncateTitle(currentTemplate.description, MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH)
    }

    const handleSelectTemplate = () => {
        // Download the currently previewed template
        if (downloadTemplateFile && currentTemplate) {
            // Check if this is a system template (it should match the pattern used in the main page)
            if (isSystemTemplateMatch(currentTemplate)) {
                // System template uses special parameters
                downloadTemplateFile(currentTemplate.id, currentTemplate.title)
                toggleDrawer()
            } else {
                // Custom template uses its own id and title
                downloadTemplateFile(currentTemplate.id, currentTemplate.title)
                toggleDrawer()
            }
        }
    }

    return (
        <Drawer
            className={classes.drawer}
            classes={{ paper: classes.drawerPaper }}
            anchor="right"
            open={isOpen}
            onClose={toggleDrawer}>
            <Box className={classes.headerBox}>
                <Box className={classes.headerContent}>
                    <Typography className={classes.headerTitle}>{getPreviewTitle()}</Typography>
                    {getPreviewDescription() && (
                        <AtlasTooltip
                            title={
                                // Prefer full description if custom template and it's longer than limit
                                currentTemplate?.id &&
                                !isSystemTemplateMatch(currentTemplate) &&
                                currentTemplate?.description?.length >
                                    MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH
                                    ? currentTemplate.description
                                    : // If system template, we don't rely on object description; show the i18n full text
                                    isSystemTemplateMatch(currentTemplate)
                                    ? currentTemplate?.description ===
                                    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION
                                        ? i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION')
                                        : i18n.t(
                                              'MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION'
                                          )
                                    : ''
                            }
                            interactive
                            placement="top">
                            <Typography className={classes.headerDescription}>
                                {getPreviewDescription()}
                            </Typography>
                        </AtlasTooltip>
                    )}
                </Box>
                <Box className={classes.btnContainer}>
                    <IconButton disableFocusRipple disableRipple onClick={() => setIsOpen(!isOpen)}>
                        <div
                            className={classes.closeButton}
                            role="button"
                            aria-label="Close"
                            tabIndex={0}
                            onClick={() => setIsOpen(!isOpen)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    setIsOpen(!isOpen)
                                }
                            }}>
                            <AtlasIcon data-testid="AtlasModal_CloseIcon" name="close" />
                        </div>
                    </IconButton>
                </Box>
            </Box>
            <Box className={classes.body}>
                <Box className={classes.contentBox}>
                    <Box className={classes.defaultTemplateSection}>
                        <Box className={classes.uploadSection}>
                            <PreviewTemplateComponent
                                imageUrlsData={imageUrlsData}
                                pageCount={pageCount}
                                loading={loading}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <div className={classes.footerSection}>
                <Box className={classes.footer}>
                    <Divider />
                    <Box className={classes.footerComp}>
                        <AddActionEditActionButton
                            key="actionCancel"
                            disableFocusRipple
                            disableRipple
                            disabled={false}
                            onClick={toggleDrawer}>
                            {i18n.t('CANCEL')}
                        </AddActionEditActionButton>
                        <AtlasButton
                            role="button"
                            onKeyPress={(e) =>
                                addKeyPressHandler(e) ? handleSelectTemplate() : null
                            }
                            disableRipple
                            className={classes.saveTemplate}
                            key="actionSave"
                            disabled={false}
                            type="submit"
                            onClick={handleSelectTemplate}
                            disableFocusRipple>
                            {i18n.t('MINUTES_TEMPLATES_DOWNLOAD_TEMPLATE')}
                        </AtlasButton>
                    </Box>
                </Box>
            </div>
        </Drawer>
    )
}

export default PreviewTemplateDrawer
